import { useMsal } from '@azure/msal-react';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { usePermissions } from '@contexts/permissions-provider/hooks/use-permissions/usePermissions';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import { useGetToken } from '@shared/hooks/use-get-token';
import * as React from 'react';

import { TrainingContentLibraryRouterProps } from './TrainingContentLibraryRouterPage.types';

const TrainingContentLibraryRouterPage = React.memo(
  (): React.ReactElement | null => {
    const { instance } = useMsal();
    const authData = useAuthenticatedUser();
    const { permissions } = usePermissions();
    const getToken = useGetToken();
    const [RemoteRouter, setRemoteRouter] =
      React.useState<React.ComponentType<TrainingContentLibraryRouterProps> | null>(
        null
      );
    const [loadError, setLoadError] = React.useState<Error | null>(null);

    React.useEffect(() => {
      const loadRouter = async (): Promise<void> => {
        try {
          const { default: Router } =
            await import('training_content_mfe/TrainingContentLibraryRouter');
          setRemoteRouter(() => Router);
        } catch (error) {
          console.error('Error loading TrainingContentLibraryRouter:', error);
          setLoadError(error as Error);
        }
      };

      loadRouter();
    }, []);

    if (loadError) {
      return <InlineFeedback type="internal-server-error" showGoHome={false} />;
    }

    if (!RemoteRouter) {
      return <ContentLoader />;
    }

    return (
      <React.Suspense fallback={<ContentLoader />}>
        <RemoteRouter
          msalInstance={instance}
          user={authData}
          permissions={permissions}
          getToken={getToken}
        />
      </React.Suspense>
    );
  }
);

TrainingContentLibraryRouterPage.displayName =
  'TrainingContentLibraryRouterPage';

export { TrainingContentLibraryRouterPage };
